import './VehicleCard.scss';
import { DriverInterface } from 'types/DriverInterface';
import { ReactComponent as CopyIcon } from 'assets/icons/copy-icon.svg';
import { ReactComponent as LockIcon } from 'assets/icons/locked.svg';
import { ReactComponent as UnlockIcon } from 'assets/icons/unlocked.svg';
import { useAppSettings } from 'context/appsettingsContext';
import { useAuth } from 'context/useAuth';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useSignalR } from 'context/useSignalR';
import axios from 'axios';
import Button from 'components/Button/Button';
import InfoCard from 'components/InfoCard/InfoCard';
import Mapview from 'components/Mapview/Mapview';
import Modal from 'components/Modal/Modal';
import { Device } from 'types/Device';
import { GetDeviceEventsStatusInfoResponse, } from 'types/DeviceEventStatusInfo';
import { CircularProgress } from '@mui/material';
import CustomOption from 'components/CustomOption/CustomOption';
import { useCollection } from 'functions/hooks/useCollection';
import { errorAsync } from 'functions/notification';
import { getDateFromTimestamp } from 'functions/deviceStatusUtils';
import { SquareArrowOutUpRightIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const NON_SERIAL_NUMBER = "Link Device";

export interface VehicleProps {
  /** Device status */
  deviceActive: boolean;
  /** Vehicle Drivers */
  drivers: DriverInterface[];
  /** Locked status */
  isLocked: boolean;
  /** Vehicle current GPS location */
  gps: { lat: number | null; lng: number | null };
  /** Vehicle make */
  make: string;
  /** Vehicle nickname */
  nickname: string;
  /** Vehicle registration number */
  registration: string;
  /** Last gps update date */
  gpsDateTime: string;
  /** Last update date */
  lastDateTime: string;
  /** Vehicle tamper status */
  tamperActive: boolean;
  /** Vehicle ID */
  vehicleId: string;
  /** Device ID */
  deviceId: string;
  /** Device Serial Number */
  deviceSerialNumber: string;
  /** Client Id */
  clientId?: string;
  /** Is marked as tracker-only device */
  isTrackerOnly?: boolean;
}

/** A UI component to render the details of a user's vehicle */
export default function VehicleCard({
  make,
  registration,
  deviceActive,
  gps,
  gpsDateTime,
  lastDateTime,
  isLocked,
  nickname,
  tamperActive,
  drivers,
  vehicleId,
  deviceId,
  deviceSerialNumber,
  clientId,
  isTrackerOnly,
}: VehicleProps) {
  const settings = useAppSettings();
  const deviceModalRef = useRef();
  const { authorisedUser } = useAuth();
  const navigate = useNavigate();
  const { signalGPS, signalLock } = useSignalR();
  const [deviceEventStatusInfo, setDeviceEventStatusInfo] = useState<GetDeviceEventsStatusInfoResponse>();
  const [sendingDeviceCommand, setSendingDeviceCommand] = useState(false);
  const [sendingAlertCommand, setSendingAlertCommand] = useState({
    AlertAura: false,
    AlertCapitalAir: false,
  });
  const [commandNotification, setCommandNotification] = useState({
    status: '',
    message: '',
  });

  const initialGPSLocation = {
    lat: gps.lat,
    lng: gps.lng,
  };

  const [GPSLocation, setGPSLocation] = useState(initialGPSLocation);
  const [lockedState, setLockedState] = useState(isLocked);
  const [gpsDate, setGpsDate] = useState(getDateFromTimestamp(gpsDateTime));
  const [lastDate, setLastDate] = useState(getDateFromTimestamp(lastDateTime));
  const [coordsCopied, setCoordsCopied] = useState(false);

  const [devices, setDevices] = useState<Device[]>([]);
  const [loadingDeviceSearch, setLoadingDeviceSearch] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<Device>({
    id: deviceId,
    active: deviceActive,
    serialNumber: deviceSerialNumber || NON_SERIAL_NUMBER
  });
  const [currentDevice, setCurrentDevice] = useState<Device>({
    id: deviceId,
    active: deviceActive,
    serialNumber: deviceSerialNumber || NON_SERIAL_NUMBER
  });
  const devicesCollection = useCollection<any>('devices');
  const vehiclesCollection = useCollection<any>('vehicles');

  useEffect(() => {
    (async () => {
      if (deviceId) {

        try {
          
          const newItem = await devicesCollection.getOne(deviceId);
          const newStatus = newItem?.StatusUpdateLog;
          const newVersion = newStatus?.currentFirmwareVersion;
          const newLatLng = newItem?.LocationUpdateLog?.location?.latLng || newItem?.LastLocation?.latLng || null;
          const locationDate = getDateFromTimestamp(newItem?.LocationUpdateLog ? newItem?.LocationUpdateLog.logTimestamp : newItem?.LastLocation ? newItem?.LastLocation.updatedAtServerTimestamp : null);
    
          // status

          if (newItem?.LockResultLog) {
            setLockedState(newItem?.LockResultLog?.requestLock);
          }
          
          if (newStatus) {
            setDeviceEventStatusInfo((prevState) => ({
              ...prevState,
              applicationVersion: `${newVersion?.majorVersion}.${newVersion?.minorVersion}.${newVersion?.patchVersion}`,
              autoLockEnabledState: newStatus.autoLockPeriodSec,
              carBatteryState: newStatus.vehicleBatteryConnected,
              autoLockTimeout: newStatus.autoLockPeriodSec,
            }));
          }
    
          // location

          if (newLatLng) {
            setGPSLocation((prevState) => ({
              ...prevState,
              lat: newLatLng?.latitudeDeg || null,
              lng: newLatLng?.longitudeDeg || null,
            }));
          }

          // gps date

          if (locationDate) {
            setGpsDate(locationDate);
          }

          // last date

          if (newItem.lastMessageTimestamp) {
            setLastDate(getDateFromTimestamp(newItem.lastMessageTimestamp));
          }
    
        } catch (error) {
          console.error('Failed to update vehicle state: ', error);
        }
      }
    })();
  }, [signalLock, signalGPS, vehicleId]);

  useEffect(() => {
    getLastDeviceEventStatusInfo(deviceId);
  }, [currentDevice]);

  const handleLockUnlockClick = async (action: string) => {
    
    const lock = action.endsWith('-lock');
    const lockType = action.includes('normal') ? 'normal' : 'hijack';
    
    try {

      setSendingDeviceCommand(true);
      
      await axios.post(
        settings.handleLockCommandsUrl,
        JSON.stringify({ fromWeb: true, "deviceId": deviceId, "reqLockState": lock, "lockCode": "-", "lockType": lock ? lockType : "admin" }),
        { headers: { Authorization: `Bearer ${authorisedUser.accessToken}`, 'Content-Type': 'application/json' } }
      );
      
    } catch (error: any) {
      errorAsync(`Failed to '${ lock ? `${lockType} lock` : 'unlock' }' : ${error?.response?.data?.message || error?.response?.data?.error || error?.message || error}`, error);
    } finally {
      setSendingDeviceCommand(false);
    }
  };

  function clearAlertStatus() {
    setTimeout(() => setCommandNotification({ status: '', message: '' }), 2000);
  }

  async function handleAlertCommand(event: React.MouseEvent<HTMLElement>) {
    const target = event.target as HTMLInputElement;
    setCommandNotification({ status: '', message: '' });

    setSendingAlertCommand((prevState) => ({
      ...prevState,
      [target.id]: true,
    }));

    try {
      const response = await axios.post(
        settings.triggerManualAlertUrl,
        { deviceId: deviceId, vehicleId: vehicleId },
        { headers: { Authorization: `Bearer ${authorisedUser.accessToken}` } }
      );

      if (response?.status === 200) {
        setCommandNotification({
          status: 'success',
          message: 'Alert sent',
        });
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        setCommandNotification({
          status: 'error',
          message: error.response?.data?.message || error.message,
        });
      }
    } finally {
      setSendingAlertCommand((prevState) => ({
        ...prevState,
        [target.id]: false,
      }));
      clearAlertStatus();
    }
  }

  function copyToClipboard() {
    setCoordsCopied(true);
    navigator.clipboard.writeText(
      `Lat: ${GPSLocation.lat}, Lng: ${GPSLocation.lng}`
    );
    setTimeout(() => {
      setCoordsCopied(false);
    }, 1000);
  }

  async function getLastDeviceEventStatusInfo(deviceId: string) {
    try {
      const device = await devicesCollection.getOne(deviceId) || {};
      const status = device?.StatusUpdateLog;

      if (status) {
        setDeviceEventStatusInfo({
          applicationVersion: `${status.currentFirmwareVersion?.majorVersion}.${status.currentFirmwareVersion?.minorVersion}.${status.currentFirmwareVersion?.patchVersion}` ,
          autoLockEnabledState: status.autoLockPeriodSec,
          carBatteryState: status.vehicleBatteryConnected,
          autoLockTimeout: status.autoLockPeriodSec,
        });
      } else {
        console.warn('status is falsy: ', status);
      }
  
    } catch (error: any) {
      //if (axios.isAxiosError(error)) {
        console.error(error?.response?.data?.message ?? error?.response?.statusText ?? error?.message ?? 'Failed to get device status info'); // TODO:Until we sort out notifications
      //}
    }
  }

  const changeLinkedDevice = async () => {
    try {
      console.log('in changeLinkedDevice');

      const vehicle = await vehiclesCollection.getOne(vehicleId) || {};
      vehicle.deviceId = selectedDevice.serialNumber === NON_SERIAL_NUMBER ? undefined : selectedDevice.serialNumber;
      await vehiclesCollection.updateOne(vehicleId, vehicle);
      
      // reconcile current and selected

      setSelectedDevice(selectedDevice);
      setCurrentDevice(selectedDevice);

      // close search modal

      (deviceModalRef.current as any)?.close();

      // clear devices

      setDevices([]);

    } catch (error: any) {
      //if (axios.isAxiosError(error)) {
        alert(error?.response?.data?.message ?? error?.response?.statusText ?? error?.message ?? 'Failed to change device'); // TODO:Until we sort out notifications
      //}
    }
  }

  let searchDevices: Device[] = [];

  async function getDevices(searchText: string) {
    try {

      // show progress

      setLoadingDeviceSearch(true);

      // fetch

      if (!searchDevices.length) {
        searchDevices = (await devicesCollection.queryMany()).map(x => ({
          id: x.id,
          active: true,
          serialNumber: x.id || NON_SERIAL_NUMBER,
        }));
      }

      const filteredDevices = searchDevices.filter(x => x.serialNumber.includes(searchText));

      setDevices(filteredDevices);

    } catch (error) {

      //if (axios.isAxiosError(error)) {
        console.log(error); //TODO: User has to know
      //}

    } finally {

      // hide progress

      setLoadingDeviceSearch(false);
    }
  }

  function handleDeviceSearch(event: ChangeEvent<HTMLInputElement>) {

    var { value } = event.target;

    // remember input

    setSelectedDevice(prevState => ({
      ...prevState,
      id: '',
      active: false,
      serialNumber: value || NON_SERIAL_NUMBER,
    }));

    if (value.length >= 3) {
      getDevices(value);
    } else {
      setDevices([]); // clear
    }
  }

  function handleDeviceSelect(device: Device) {
    
    // remember input

    setSelectedDevice(prevState => ({
      ...prevState,
      ...device
    }));
  }

  function abortDeviceSearch() {
    
    // revert back to current

    setSelectedDevice(prevState => ({
      ...prevState,
      ...currentDevice
    }));

    // clear devices

    setDevices([]);
  }

  async function streamDevice(deviceId: string) {
    const deviceTest = await devicesCollection.getOne(deviceId);
    if (deviceTest != null) {
      devicesCollection.getOneStream(deviceId, newItem => {
        try {
          if (!newItem) {
            console.error('No device found');
            return;
          }
          
          const newStatus = newItem?.StatusUpdateLog;
          const newVersion = newStatus?.currentFirmwareVersion;
          const newLatLng = newItem?.LastValidLocationUpdateLog?.location?.latLng || newItem?.LastLocation?.latLng || newItem?.LocationUpdateLog?.location?.latLng;
          const locationDate = getDateFromTimestamp(newItem?.LastValidLocationUpdateLog?.location?.fixTimestampEpochMilliSec || newItem?.LastLocation?.fixTimestampEpochMilliSec || newItem?.LocationUpdateLog?.location?.fixTimestampEpochMilliSec);
    
          // status

          if (newItem?.LastSuccessfulLockResultLog) {
            setLockedState(newItem?.LastSuccessfulLockResultLog?.requestLock);
          } else if (newItem?.LockResultLog) {
            setLockedState(newItem?.LockResultLog?.requestLock);
          }
          
          if (newStatus) {
            setDeviceEventStatusInfo((prevState) => ({
              ...prevState,
              applicationVersion: `${newVersion?.majorVersion}.${newVersion?.minorVersion}.${newVersion?.patchVersion}`,
              autoLockEnabledState: newStatus.autoLockPeriodSec,
              carBatteryState: newStatus.vehicleBatteryConnected,
              autoLockTimeout: newStatus.autoLockPeriodSec,
            }));
          }
    
          // location

          if (newLatLng) {
            setGPSLocation((prevState) => ({
              ...prevState,
              lat: newLatLng?.latitudeDeg || null,
              lng: newLatLng?.longitudeDeg || null,
            }));
          }

          // date

          if (locationDate) {
            setGpsDate(locationDate);
          }
    
        } catch (error) {
          console.error('Failed to update vehicle state: ', error);
        }

      });
    }
  }
  
  useEffect(() => {
    if (deviceId) {
      streamDevice(deviceId);
    }
  }, [deviceId, devicesCollection]);

  return (
    <div className='vehicle-card' aria-label='vehicle card'>
      <div className='vehicle-card__content'>
        <div
          className='vehicle-card__nickname'
          aria-label='vehicle card nickname'
        >
          {nickname ?? make}
        </div>
        <div className='vehicle-card_' aria-label='vehicle card registration'>
          {registration}
        </div>
        <br/>
        <div className="vehicle-card__infoContainer">
          <div className='vehicle-card__infoLabel'>
            Device ID
          </div>
          <div className='vehicle-card__infoValue' title={selectedDevice.serialNumber || NON_SERIAL_NUMBER}>
            <table cellPadding={0} cellSpacing={0} style={{margin:0,padding:0,border:0,boxShadow:'none'}}>
                <tbody style={{margin:0,padding:0,border:0,boxShadow:'none'}}>
                    <tr style={{margin:0,padding:0,border:0,boxShadow:'none'}}>
                        <td style={{margin:0,padding:0,border:0,boxShadow:'none'}}>
                            <Modal
                                innerHeight='25rem'
                                ref={deviceModalRef}
                                onClose={abortDeviceSearch}
                                title={currentDevice.serialNumber || NON_SERIAL_NUMBER}
                                trigger={
                                    <small className='editable' style={{ display: 'block', width:'100%' }}>
                                    {currentDevice.serialNumber || NON_SERIAL_NUMBER}
                                    </small>
                                }>
                                <div className='form-field-search'>
                                    <div className='form-field'>
                                    <small>
                                        Type a least 3 characters for matches to be listed below
                                    </small>
                                    <input
                                        id='Device'
                                        autoComplete='off'
                                        placeholder='Type to select'
                                        onChange={handleDeviceSearch}
                                        title={selectedDevice.serialNumber === NON_SERIAL_NUMBER ? undefined : selectedDevice.serialNumber}
                                        value={selectedDevice.serialNumber === NON_SERIAL_NUMBER ? undefined : selectedDevice.serialNumber}
                                        className='vehicle-card__deviceSerialNumberDropdown'
                                    />
                                    </div>
                                    <div className='results' style={{ position:'static', height: '13.5rem', border: '1px solid rgb(194, 198, 199)' }}>
                                    {loadingDeviceSearch ? (
                                        <div className='results-loader'>
                                        <CircularProgress size='2rem' color='inherit' />
                                        </div>
                                    ) : (
                                        <>
                                        {
                                            devices.map(
                                            (device: Device) =>
                                                <CustomOption
                                                key={device.id}
                                                className='option'
                                                id={device.id.toString()}
                                                value={device.serialNumber || NON_SERIAL_NUMBER}
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => handleDeviceSelect(device)}
                                                >
                                                {device.serialNumber || NON_SERIAL_NUMBER}
                                                </CustomOption>
                                            )
                                        }
                                        </>
                                    )}
                                    </div>
                                    <Button
                                    label='OK'
                                    fullWidth={true}
                                    onClick={changeLinkedDevice}
                                    disabled={!devices.find(x => x.serialNumber?.toLocaleLowerCase() === selectedDevice.serialNumber?.toLocaleLowerCase())}/>
                                </div>
                            </Modal>
                        </td>
                        <td style={{margin:0,padding:0,border:0,boxShadow:'none',width:'1%',whiteSpace:'nowrap'}}>
                            <a style={{cursor:'pointer'}} onClick={() => navigate(`/agent/mgt-devices/${selectedDevice.serialNumber}?fromclientid=${clientId}`)} title='Go to Device'>
                                <SquareArrowOutUpRightIcon/>
                            </a>
                        </td>
                    </tr>
                </tbody>
            </table>
          </div>
        </div>
        <div style={{ opacity: selectedDevice.serialNumber === NON_SERIAL_NUMBER ? 0 : 1 }} className="vehicle-card__infoContainer">
          <div className='vehicle-card__infoLabel'>
            Last Location Update
          </div>
          <div className='vehicle-card__infoValue'>
            <small>{gpsDate?.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric', })}</small> - <small>{gpsDate?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</small>
          </div>
        </div>
        <div style={{ opacity: selectedDevice.serialNumber === NON_SERIAL_NUMBER ? 0 : 1 }} className="vehicle-card__infoContainer">
          <div className='vehicle-card__infoLabel'>
            Last Update
          </div>
          <div className='vehicle-card__infoValue'>
            <small>{lastDate?.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric', })}</small> - <small>{lastDate?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</small>
          </div>
        </div>
        <div style={{ opacity: selectedDevice.serialNumber === NON_SERIAL_NUMBER ? 0 : 1 }} className="vehicle-card__infoContainer">
          <div className='vehicle-card__infoLabel'>
            Firmware Version
          </div>
          <div className='vehicle-card__infoValue'>
            <small style = {{ color: deviceEventStatusInfo?.applicationVersion.includes("No") ? "orange" : "black" }}>
              {deviceEventStatusInfo?.applicationVersion} &nbsp;
            </small>
          </div>
        </div>
        <div style={{ opacity: selectedDevice.serialNumber === NON_SERIAL_NUMBER ? 0 : 1 }} className="vehicle-card__infoContainer">
          <div className='vehicle-card__infoLabel'>
            Auto-Lock State
          </div>
          <div className='vehicle-card__infoValue'>
            <small style={deviceEventStatusInfo?.autoLockEnabledState === 0 ? {color:"orange"} : {color:"green"}}>
              {deviceEventStatusInfo?.autoLockEnabledState === 0 ? "Disabled" : "Enabled"}
            </small>
          </div>
        </div>
      </div>

      {deviceActive && (
        <div className='vehicle-card__map'>
          {GPSLocation.lat === null && GPSLocation.lng === null && (
            <div
              className='info-box map-view'
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              GPS coordinates unavailable
            </div>
          )}
          {GPSLocation.lat !== null && GPSLocation.lng !== null && (
            <Mapview
              vehicle={{
                lat: GPSLocation.lat,
                lng: GPSLocation.lng,
              }}
              tamperAlert={tamperActive}
              isLocked={lockedState}
              commandNotification={commandNotification}
              commandButtons={
                <>
                  <Button
                    label={
                      sendingAlertCommand.AlertAura
                        ? 'Sending alert'
                        : 'Alert Aura'
                    }
                    outlineWhite
                    small
                    id='AlertAura'
                    onClick={handleAlertCommand}
                    disabled={sendingAlertCommand.AlertAura}
                  />
                  <Button
                    id='AlertCapitalAir'
                    label={
                      sendingAlertCommand.AlertCapitalAir
                        ? 'Sending alert'
                        : 'Capital Air'
                    }
                    outlineWhite
                    small
                    style={{ display: 'none' }}
                    onClick={handleAlertCommand}
                    disabled={sendingAlertCommand.AlertCapitalAir}
                  />
                </>
              }
            />
          )}
        </div>
      )}
      {GPSLocation.lat !== null && GPSLocation.lng !== null && (
        <div className='coords'>
          <div className='coords__group'>
            <small>Lat: {GPSLocation.lat}</small>
            <small>Lng: {GPSLocation.lng}</small>
          </div>
          <button
            aria-label='copy coordinates'
            className='coords__button'
            onClick={() => copyToClipboard()}
            disabled={coordsCopied}
          >
            {coordsCopied ? 'Copied to clipboard' : 'Copy coordinates'}
            <CopyIcon />
          </button>
        </div>
      )}

      {deviceActive && (
        <div className='vehicle-card__actions'>
            
            
          {/* <Modal
            title='Call Driver'
            styleClass='width-full'
            trigger={<Button label='Call driver' fullWidth outline small />}
          >
            {drivers[0] && (
              <InfoCard
                title={`${drivers[0].firstName} ${drivers[0].lastName}`}
                driverDetails={drivers[0]}
              />
            )}
          </Modal> */}

            {isTrackerOnly != true &&
              <div style={{fontSize: 'small'}}>
                  <Button
                      label={'Normal Lock'}
                      fullWidth
                      outline
                      icon
                      small
                      iconComponent={<LockIcon />}
                      disabled={sendingDeviceCommand}
                      onClick={() => handleLockUnlockClick('normal-lock')}
                      />
                  <Button
                      label={'Hijack Lock'}
                      fullWidth
                      outline
                      icon
                      small
                      iconComponent={<LockIcon />}
                      disabled={sendingDeviceCommand}
                      onClick={() => handleLockUnlockClick('hijack-lock')}
                      />
                  <Button
                      label={'Unlock'}
                      fullWidth
                      outline
                      icon
                      small
                      iconComponent={<UnlockIcon />}
                      disabled={sendingDeviceCommand}
                      onClick={() => handleLockUnlockClick('unlock')}
                      />
              </div>
            }
        </div>
      )}
      {/* {!deviceActive && (
        <div className='vehicle-card__actions'>
          <Button label='Reactivate' fullWidth outline />
        </div>
      )} */}

    </div>
  );
}
